import React, { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useStatusStyle } from '@europrocurement/l2d-modules/hooks';
import { ReactGridContainer } from '@europrocurement/flexy-components';
import { type ResizeHandleAxis } from 'react-grid-layout';
import { faFolder } from '@fortawesome/sharp-regular-svg-icons';
import { useSnackbar } from 'notistack';
import { DOSSIERS_OPEN_SEARCH_SLICE_NAME, useFormaliteService } from '@europrocurement/l2d-domain';
import {
    VALIDATOR_TYPE_GREFFE,
    VALIDATOR_TYPE_GREFFE_INPI,
} from '@europrocurement/l2d-modules/modules/Dossiers';
import { DossierOpenSearchJsonldOpensearchDossierValideursStatusRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DashboardStatCardTotalBar from '../molecules/DashboardStatCardTotalBar/DashboardStatCardTotalBar';
import WidgetWithAction from '../molecules/WidgetWithAction/WidgetWithAction';
import { type DashboardFiltersType } from '../../containers/FormalistDashboard/FormalistDashboard';

type GreffesStatusWidgetDashboardProps = {
    localStorageName: string;
    formalistIds: number[];
    filters: DashboardFiltersType;
    listeDossiersPath: string;
    showResetButton?: boolean;
    formalistName?: string;
};

const RESIZE_HANDLES = ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'] as ResizeHandleAxis[];

// Récupérer les valideurs de type greffe liés aux dossiers
const GreffesStatusWidgetDashboard: React.FunctionComponent<GreffesStatusWidgetDashboardProps> =
    function ({
        formalistIds,
        filters,
        listeDossiersPath,
        localStorageName,
        showResetButton,
        formalistName,
    }) {
        const { palette } = useTheme();
        const { getValidatorStatusStyle } = useStatusStyle();
        const { enqueueSnackbar } = useSnackbar();
        const { dossierOpenSearchApi } = useFormaliteService();
        const dispatch = useDispatch();
        const navigate = useNavigate();

        const handleGreffeStatusClick = (statusId?: number | null) => {
            if (!statusId) return;

            dispatch({ type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/resetmainFilter` });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'formalisteId',
                    value: formalistIds,
                },
            });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: { key: 'dateCreation', value: filters },
            });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'statutValideurId',
                    value: [statusId],
                },
            });

            if (formalistName) {
                dispatch({
                    type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilterLabel`,
                    payload: { key: 'formalisteId', label: formalistName },
                });
            }

            navigate(`/${listeDossiersPath}`);
        };

        const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
        const [hasFetched, setHasFetched] = useState<boolean>(false);
        const [statusGreffes, setStatusGreffes] = useState<
            DossierOpenSearchJsonldOpensearchDossierValideursStatusRead[]
        >([]);

        const maxXindexCard = 5;

        const fetchGreffesStatus = async () => {
            if (isSubmitting && !formalistIds) return;

            setIsSubmitting(true);
            const dossierStatusResponse =
                await dossierOpenSearchApi.apiOpensearchValideursStatusGetCollection({
                    formalisteId: formalistIds,
                    itemsPerPage: 20,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                });

            setIsSubmitting(false);
            setHasFetched(true);
            if (dossierStatusResponse.status === 200) {
                setStatusGreffes(
                    dossierStatusResponse.data['hydra:member']
                        .filter(
                            (status) =>
                                [VALIDATOR_TYPE_GREFFE_INPI, VALIDATOR_TYPE_GREFFE].includes(
                                    status.idValideur as number,
                                ) &&
                                status.sortOrder != null &&
                                status.libelleStatus !== 'Validé',
                        )
                        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
                );
            } else {
                enqueueSnackbar('Une erreur est survenue lors de la récupération des dossiers', {
                    variant: 'error',
                });
                setStatusGreffes([]);
            }
        };

        useEffect(() => {
            fetchGreffesStatus();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formalistIds, filters.startDate, filters.endDate]);

        if (!hasFetched) return null;

        // Pure layout computation — recap card at x=0..2, status items fill x=3..5 in row 0 then full rows
        let xIndex = 2;
        let yIndex = 0;
        const statusLayoutProps = statusGreffes.map((statusGreffe) => {
            if (xIndex !== 0 && xIndex % maxXindexCard === 0) {
                // In the first row there is a space for 3 items, in the others row there is space for 6 items
                xIndex = -1;
                yIndex++;
            }
            xIndex++;
            return {
                i: `${localStorageName}_${statusGreffe.libelleValideur}_${statusGreffe.idStatus}`,
                x: xIndex,
                y: yIndex,
                w: 1,
                h: 1,
                isResizable: true,
                resizeHandles: ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'] as ResizeHandleAxis[],
            };
        });

        return (
            <ReactGridContainer
                localStorageName={localStorageName}
                gridconfig={{ cols: 6, rowHeight: 200 }}
                showResetButton={showResetButton}
                layoutProps={[
                    {
                        i: `${localStorageName}_recap`,
                        x: 0,
                        y: 0,
                        w: 3,
                        h: 1,
                        isResizable: true,
                        resizeHandles: RESIZE_HANDLES,
                    },
                    ...statusLayoutProps,
                ]}
            >
                {(() => (
                    <Box
                        key={`${localStorageName}_recap`}
                        sx={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: palette.background.paper,
                            borderRadius: 2,
                            boxShadow: `0px 2px 2.5px 0px ${palette.background.shadow}`,
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                        }}
                    >
                        <DashboardStatCardTotalBar
                            items={statusGreffes.map((statusGreffe) => ({
                                id: statusGreffe.statusFlag,
                                libelle: statusGreffe.libelleStatus,
                                childsCount: statusGreffe.childsCount || 0,
                                key: `${statusGreffe.idValideur}_${statusGreffe.idStatus}`,
                            }))}
                            title="Suivi greffes — flux actif"
                            icon={faFolder}
                            handleItemClick={handleGreffeStatusClick}
                            handleStyleStatus={getValidatorStatusStyle}
                        />
                    </Box>
                ))()}

                {statusGreffes.map((statusGreffe) => {
                    const { paletteString, namedIcon } = getValidatorStatusStyle(
                        statusGreffe.statusFlag as number,
                    );
                    return (
                        <Box
                            key={`${localStorageName}_${statusGreffe.libelleValideur}_${statusGreffe.idStatus}`}
                            sx={{ height: '100%' }}
                        >
                            <WidgetWithAction
                                title={
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography
                                            variant="l2dh6"
                                            fontWeight="bold"
                                        >
                                            {statusGreffe.libelleStatus}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color={palette.text.secondary}
                                        >
                                            {statusGreffe.libelleValideur}
                                        </Typography>
                                    </Box>
                                }
                                icon={namedIcon}
                                actionMessage="Voir"
                                actionCallback={async () =>
                                    handleGreffeStatusClick(statusGreffe.idStatus as number)
                                }
                                data={statusGreffe.childsCount || '0'}
                                color={paletteString}
                            />
                        </Box>
                    );
                })}
            </ReactGridContainer>
        );
    };

export default GreffesStatusWidgetDashboard;
